import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Input } from "@/components/ui/input";
import { MapPin, Search, Loader2 } from "lucide-react";

export function MunicipalitySelector({ 
  value, 
  onChange, 
  onLocationSelect,
  placeholder = "Buscar municipio...",
  className = "",
}) {
  const [search, setSearch] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update search when value changes externally
  useEffect(() => {
    if (value && value !== search) {
      setSearch(value);
    }
  }, [value]);

  // Search municipalities from API
  useEffect(() => {
    if (search.length < 2) {
      setResults([]);
      return;
    }

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/municipalities/search`, {
          params: { q: search, limit: 15 }
        });
        setResults(res.data.municipalities || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [search]);

  const handleSelect = (municipality) => {
    setSearch(municipality.name);
    setIsOpen(false);
    onChange?.(municipality.name);
    onLocationSelect?.(municipality.lat, municipality.lng, municipality.name);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setIsOpen(true);
    onChange?.(val);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={search}
          onChange={handleInputChange}
          onFocus={() => search.length >= 2 && setIsOpen(true)}
          placeholder={placeholder}
          className="pl-9 pr-4"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute z-[200] mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-100 max-h-60 overflow-y-auto">
          {results.map((m, idx) => (
            <button
              key={`${m.name}-${m.provincia}-${idx}`}
              type="button"
              onClick={() => handleSelect(m)}
              className="w-full px-4 py-3 flex items-start gap-3 hover:bg-[var(--cream-light)] transition-colors text-left border-b border-gray-50 last:border-0"
            >
              <MapPin className="w-4 h-4 text-[var(--coral)] mt-0.5 shrink-0" />
              <div>
                <p className="font-heading text-sm font-semibold text-[var(--navy)]">
                  {m.name}
                </p>
                <p className="font-body text-xs text-gray-400">
                  {m.provincia}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {isOpen && search.length >= 2 && results.length === 0 && !loading && (
        <div className="absolute z-[200] mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-100 p-4 text-center">
          <p className="font-body text-sm text-gray-500">
            No se encontró "{search}"
          </p>
          <p className="font-body text-xs text-gray-400 mt-1">
            Puedes escribir el nombre manualmente
          </p>
        </div>
      )}
    </div>
  );
}

export default MunicipalitySelector;
